import {
  type Drop,
  getMappingDisplay,
  getMappingListDisplay,
  getStringListDisplay,
} from '@/api/hooks/useOrderDetails'
import type { components } from '@/types/api/v1'
import { formatVolume } from '@/utils/stringFormatters'
import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

type VolumeUnits = components['schemas']['VolumeUnits']

export interface DropCardProps {
  drop: Drop
  units?: VolumeUnits | null
}

export function DropCard({ drop, units }: DropCardProps) {
  const productName = getMappingDisplay(drop.product)
  const siteName = getMappingDisplay(drop.site)
  const volume = drop.volume
  const tankId = (drop.site as { tank_id?: string | null })?.tank_id

  return (
    <Vertical
      className='bg-1 rounded-md p-4 overflow-visible'
      gap={16}
      style={{ height: '100%' }}
    >
      {/* Destination and Product info */}
      <Horizontal alignItems='flex-start' justifyContent='space-between'>
        {/* Destination on left */}
        <Vertical>
          <Texto
            className='text-muted-foreground uppercase tracking-wide mb-1'
            style={{ fontSize: 11, fontWeight: 600 }}
          >
            Destination
          </Texto>
          <Texto style={{ fontSize: 20, fontWeight: 700 }}>{siteName}</Texto>
        </Vertical>

        {/* Product/Tank/Volume on right */}
        <Vertical alignItems='flex-end'>
          <Texto
            className='text-muted-foreground uppercase tracking-wide mb-1'
            style={{ fontSize: 11, fontWeight: 600 }}
          >
            {productName}
            {tankId && ` (Tank ${tankId})`}
          </Texto>
          <Texto style={{ fontSize: 20, fontWeight: 700 }}>
            {formatVolume(volume, units)}
          </Texto>
        </Vertical>
      </Horizontal>

      {/* Loading info section - pushed to bottom */}
      <div style={{ overflow: 'visible', marginTop: 'auto' }}>
        <Texto
          className='uppercase tracking-wide mb-1'
          style={{ fontSize: 12, fontWeight: 600 }}
        >
          Loading Info
        </Texto>
        <LoadingInfoTable loads={drop.loads} />
      </div>
    </Vertical>
  )
}

interface LoadingInfoTableProps {
  loads: Drop['loads']
}

interface LoadRow {
  id: string
  product: string
  terminal: string
  supplier: string
  priceType: string
  contract: string
}

function LoadingInfoTable({ loads }: LoadingInfoTableProps) {
  const rowData = useMemo<LoadRow[]>(
    () =>
      loads.map((load, index) => ({
        id: `load-${index}`,
        product: getMappingListDisplay(load.products),
        terminal: getMappingListDisplay(load.terminals),
        supplier: getMappingListDisplay(load.suppliers),
        priceType: getStringListDisplay(load.price_types),
        contract: getStringListDisplay(load.contracts),
      })),
    [loads],
  )

  const columnDefs = useMemo<ColDef<LoadRow>[]>(
    () => [
      {
        headerName: 'Product',
        field: 'product',
        flex: 1,
        minWidth: 100,
        headerTooltip: 'Product being loaded',
      },
      {
        headerName: 'Terminal',
        field: 'terminal',
        flex: 1,
        minWidth: 100,
        headerTooltip: 'Loading terminal location',
      },
      {
        headerName: 'Supplier',
        field: 'supplier',
        flex: 1,
        minWidth: 100,
        headerTooltip: 'Product supplier',
      },
      {
        headerName: 'Price Type',
        field: 'priceType',
        flex: 1,
        minWidth: 80,
        headerTooltip: 'Pricing type for this load',
      },
      {
        headerName: 'Contract',
        field: 'contract',
        flex: 1,
        minWidth: 80,
        headerTooltip: 'Contract for this load, if applicable.',
      },
    ],
    [],
  )

  return (
    <div className='loading-info-grid rounded overflow-hidden'>
      <GraviGrid
        rowData={rowData}
        columnDefs={columnDefs}
        sideBar={false}
        agPropOverrides={{
          getRowId: (params) => params.data.id,
          suppressRowClickSelection: true,
          rowGroupPanelShow: 'never',
          headerHeight: 32,
          rowHeight: 32,
          domLayout: 'autoHeight',
          defaultColDef: {
            resizable: false,
            sortable: false,
            suppressMovable: true,
          },
          overlayNoRowsTemplate:
            '<span style="color: var(--muted-foreground); font-size: 13px;">No loading info recorded</span>',
        }}
      />
    </div>
  )
}
